/**
 * Payment Form Component using Stripe Embedded Checkout
 * Following Stripe Embedded Checkout quickstart: https://docs.stripe.com/checkout/embedded/quickstart
 */

import React, { useState, useMemo, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js'
import { invoicesAPI, stripeAPI } from '../../utils/scheduler/api'

export default function PaymentForm({ invoiceId, onSuccess, onCancel }) {
  const [error, setError] = useState('')
  const [stripePromise, setStripePromise] = useState(null)
  const [loadingStripe, setLoadingStripe] = useState(true)

  // Fetch Stripe publishable key from backend at runtime
  useEffect(() => {
    let cancelled = false

    async function loadStripeKey() {
      try {
        setLoadingStripe(true)
        
        // Try to get from API endpoint first (production)
        try {
          const config = await stripeAPI.getConfig()
          if (config.success && config.publishableKey) {
            if (!cancelled) {
              setStripePromise(loadStripe(config.publishableKey))
              setLoadingStripe(false)
              return
            }
          }
        } catch (apiError) {
          console.warn('Could not fetch Stripe key from API, trying env var:', apiError)
        }

        // Fallback to environment variable (local development)
        const envKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
        if (envKey && envKey.trim() !== '') {
          if (!cancelled) {
            setStripePromise(loadStripe(envKey))
            setLoadingStripe(false)
            return
          }
        }

        // No key found
        if (!cancelled) {
          setStripePromise(null)
          setLoadingStripe(false)
        }
      } catch (err) {
        console.error('Error loading Stripe:', err)
        if (!cancelled) {
          setStripePromise(null)
          setLoadingStripe(false)
        }
      }
    }

    loadStripeKey()

    return () => {
      cancelled = true
    }
  }, [])

  // fetchClientSecret function that Stripe will call
  // This follows the Stripe Embedded Checkout pattern from the quickstart guide
  const fetchClientSecret = async () => {
    if (!invoiceId) {
      const errorMsg = 'Invoice ID is required'
      setError(errorMsg)
      throw new Error(errorMsg)
    }

    try {
      const response = await invoicesAPI.createPaymentSession(invoiceId)
      if (response.success && response.client_secret) {
        // Clear any previous errors on success
        setError('')
        return response.client_secret
      } else {
        const errorMsg = response.error || 'Failed to create payment session'
        setError(errorMsg)
        throw new Error(errorMsg)
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to initialize payment'
      setError(errorMsg)
      console.error('Error creating payment session:', err)
      throw err
    }
  }

  // Handle checkout completion
  const handleComplete = async () => {
    // Payment successful - automatically sync payment status from Stripe
    // This ensures the invoice is marked as paid immediately, even if webhook hasn't processed yet
    try {
      console.log('Payment completed, syncing payment status for invoice', invoiceId)
      
      // Try to sync payment status immediately (available to invoice owner)
      let synced = false
      try {
        const syncResult = await invoicesAPI.syncPayment(invoiceId)
        if (syncResult.success && syncResult.synced) {
          console.log('Payment status synced successfully:', syncResult.payment_status)
          synced = true
        } else {
          console.log('Payment status not yet updated, will poll for updates')
        }
      } catch (syncError) {
        // If sync fails, that's okay - we'll poll
        console.warn('Could not sync payment status immediately:', syncError.message)
      }
      
      // If not synced immediately, poll a few times (webhook might be delayed)
      if (!synced) {
        let attempts = 0
        const maxAttempts = 5
        const pollInterval = 2000 // 2 seconds
        
        while (attempts < maxAttempts && !synced) {
          await new Promise(resolve => setTimeout(resolve, pollInterval))
          attempts++
          
          try {
            const syncResult = await invoicesAPI.syncPayment(invoiceId)
            if (syncResult.success && syncResult.synced) {
              console.log(`Payment status synced after ${attempts} attempt(s)`)
              synced = true
              break
            }
          } catch (pollError) {
            console.warn(`Poll attempt ${attempts} failed:`, pollError.message)
          }
        }
        
        if (!synced) {
          console.log('Payment status not synced after polling, webhook will update it shortly')
        }
      }
      
      // Call onSuccess callback to refresh the invoice
      if (onSuccess) {
        await onSuccess()
      }
    } catch (error) {
      console.error('Error in payment completion handler:', error)
      // Still call onSuccess to refresh, webhook will update eventually
      if (onSuccess) {
        await onSuccess()
      }
    }
  }

  if (loadingStripe) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="ml-3 text-sm text-gray-600">Loading payment form...</span>
        </div>
      </div>
    )
  }

  if (!stripePromise) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <div className="text-sm font-medium text-red-800 mb-2">
            Stripe is not configured
          </div>
          <div className="text-sm text-red-700 space-y-2">
            <p>The Stripe publishable key is missing. To configure Stripe payments:</p>
            <div className="ml-4 space-y-1 text-xs">
              <p><strong>For local development:</strong></p>
              <ol className="list-decimal list-inside ml-2 space-y-1">
                <li>Create a <code className="bg-red-100 px-1 rounded">.env</code> file in the project root</li>
                <li>Add: <code className="bg-red-100 px-1 rounded">VITE_STRIPE_PUBLISHABLE_KEY=pk_test_...</code></li>
                <li>Restart your development server</li>
              </ol>
              <p className="mt-2"><strong>For production (Cloudflare Pages):</strong></p>
              <ol className="list-decimal list-inside ml-2 space-y-1">
                <li>Open <code className="bg-red-100 px-1 rounded">wrangler.toml</code> in your project root</li>
                <li>Under the <code className="bg-red-100 px-1 rounded">[vars]</code> section, add:
                  <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
STRIPE_PUBLISHABLE_KEY = "pk_test_..."  # or "pk_live_..." for production
                  </pre>
                </li>
                <li><strong>Important:</strong> Use <code className="bg-red-100 px-1 rounded">STRIPE_PUBLISHABLE_KEY</code> (not VITE_STRIPE_PUBLISHABLE_KEY) - this is fetched at runtime from the backend</li>
                <li>Redeploy your site after adding the variable</li>
              </ol>
              <div className="mt-2 p-2 rounded bg-blue-50 border border-blue-200">
                <p className="font-medium text-blue-800">ℹ️ How it works:</p>
                <p className="text-blue-700 text-xs">
                  The publishable key is now fetched from the backend API at runtime, so it doesn't need to be available at build time. 
                  This makes it work reliably with Cloudflare Pages.
                </p>
              </div>
            </div>
            <p className="mt-3 text-xs">
              Get your Stripe keys from{' '}
              <a 
                href="https://dashboard.stripe.com/apikeys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline hover:text-red-900"
              >
                Stripe Dashboard → API keys
              </a>
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{
          fetchClientSecret,
          onComplete: handleComplete
        }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  )
}
